import { type UserRole } from './user.model';

/** Pages of the Portal-SPA a permission can be set for (API: PortalPage, camelCase). */
export type PortalPage =
  | 'dashboard'
  | 'employees'
  | 'time'
  | 'absences'
  | 'shifts'
  | 'shiftSwaps'
  | 'monthClose'
  | 'exports'
  | 'terminalScans'
  | 'terminals'
  | 'settings';

/** What a user may do on a page. Ordered: write includes read. */
export type PermissionLevel = 'none' | 'read' | 'write';

/** Display order of the pages, same as the navigation. */
export const PORTAL_PAGES: PortalPage[] = [
  'dashboard',
  'employees',
  'time',
  'absences',
  'shifts',
  'shiftSwaps',
  'monthClose',
  'exports',
  'terminalScans',
  'terminals',
  'settings',
];

export const PAGE_LABELS: Record<PortalPage, string> = {
  dashboard: 'Dashboard',
  employees: 'Mitarbeitende',
  time: 'Zeiterfassung',
  absences: 'Abwesenheiten',
  shifts: 'Schichtplanung',
  shiftSwaps: 'Schichttausch',
  monthClose: 'Monatsabschluss',
  exports: 'Exporte',
  terminalScans: 'Terminal-Scans',
  terminals: 'Terminals',
  settings: 'Einstellungen',
};

export const LEVEL_LABELS: Record<PermissionLevel, string> = {
  none: 'Keine',
  read: 'Lesen',
  write: 'Schreiben',
};

export const PERMISSION_LEVELS: PermissionLevel[] = ['none', 'read', 'write'];

/** One row of GET /api/mgmt/users/{employeeId}/page-permissions. */
export interface PagePermissionRow {
  page: PortalPage;
  /** What applies now: the override if there is one, else the default of the role. */
  level: PermissionLevel;
  /** What the role gives without an override. */
  defaultLevel: PermissionLevel;
  overridden: boolean;
}

export interface UserPagePermissions {
  employeeId: string;
  role: UserRole;
  pages: PagePermissionRow[];
}

/** Route of every page, used to land on the first page a user may open. */
export const PAGE_PATHS: Record<PortalPage, string> = {
  dashboard: '/dashboard',
  employees: '/employees',
  time: '/time',
  absences: '/absences',
  shifts: '/shifts',
  shiftSwaps: '/shift-swaps',
  monthClose: '/month-close',
  exports: '/exports',
  terminalScans: '/terminal-scans',
  terminals: '/terminals',
  settings: '/settings',
};

export type PagePermissionMap = Partial<Record<PortalPage, PermissionLevel>>;

const LEVEL_ORDER: Record<PermissionLevel, number> = { none: 0, read: 1, write: 2 };

/** True if `have` reaches `needed` (write includes read). */
export function reaches(have: PermissionLevel | undefined, needed: PermissionLevel): boolean {
  return LEVEL_ORDER[have ?? 'none'] >= LEVEL_ORDER[needed];
}

/**
 * What a role gets without any stored override. Only used for an API that predates `pagePermissions` in
 * GET /api/profile/me, so the SPA keeps working during a rollout; mirrors PagePermissions.DefaultFor in the API.
 */
export function roleDefaultPermissions(role: UserRole): PagePermissionMap {
  const map: PagePermissionMap = {};
  for (const page of PORTAL_PAGES) {
    map[page] =
      role === 'admin' ? 'write'
      : role === 'chef' ? (page === 'terminals' || page === 'terminalScans' ? 'none' : 'write')
      : 'none';
  }
  return map;
}
